import {
  CourseDTO,
  CourseQueryDTO,
  CourseUserDetailsDTO,
  CreateCourseDTO,
} from "@onetutor/shared/dto";
import { buildQueryString } from "../utils/api.util";
import { onetutor } from "./onetutor";

const course = onetutor.injectEndpoints({
  endpoints: (builder) => ({
    getCourses: builder.query<CourseUserDetailsDTO[], CourseQueryDTO>({
      query: (queryArgs) => buildQueryString("course", queryArgs),
      providesTags: (courses) => [
        ...(courses ?? []).map((course) => ({
          type: "course" as const,
          id: course?.id,
        })),
        { type: "course", id: "LIST" },
      ],
    }),
    getCourse: builder.query<CourseUserDetailsDTO, string>({
      query: (id) => `course/${id}`,
      providesTags: (course) => [{ type: "course", id: course?.id }],
    }),
    enrollCourse: builder.mutation<
      CourseUserDetailsDTO,
      { courseId: string; password?: string }
    >({
      query: ({ courseId, password }) => ({
        url: `course/${courseId}/enroll`,
        method: "POST",
        body: { password },
      }),
      invalidatesTags: (_result, _error, { courseId }) => [
        { type: "course", id: courseId },
      ],
    }),
    leaveCourse: builder.mutation<CourseUserDetailsDTO, string>({
      query: (id) => ({
        url: `course/${id}/enroll`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, id) => [{ type: "course", id }],
    }),
    deleteCourse: builder.mutation<void, string>({
      query: (id) => ({
        url: `course/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, id) => [{ type: "course", id }],
    }),
    updateCourse: builder.mutation<
      void,
      { course: CreateCourseDTO; courseId: string }
    >({
      query: ({ course, courseId }) => ({
        url: `course/${courseId}`,
        method: "PUT",
        body: course,
      }),
      invalidatesTags: (_result, _error, { courseId }) => [
        { type: "course", id: courseId },
      ],
    }),
    createCourse: builder.mutation<CourseUserDetailsDTO, CreateCourseDTO>({
      query: (course) => ({
        url: "course",
        method: "POST",
        body: course,
      }),
      invalidatesTags: [{ type: "course", id: "LIST" }],
    }),
    getDashboard: builder.query<string, string>({
      query: (id) => ({
        url: `course/${id}/dashboard`,
        method: "GET",
        responseHandler: (response) => response.text(),
      }),
    }),
    adminEnrollUserToCourse: builder.mutation<
      void,
      { courseId: string; userId: string }
    >({
      query: ({ courseId, userId }) => ({
        url: `course/${courseId}/enroll/${userId}`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, { courseId, userId }) => [
        { type: "course", id: courseId },
        { type: "user", id: userId },
      ],
    }),
    adminUnenrollUserFromCourse: builder.mutation<
      void,
      { courseId: string; userId: string }
    >({
      query: ({ courseId, userId }) => ({
        url: `course/${courseId}/enroll/${userId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { courseId, userId }) => [
        { type: "course", id: courseId },
        { type: "user", id: userId },
      ],
    }),
    getUserEnrolledCourses: builder.query<CourseDTO[], string>({
      query: (userId) => `user/${userId}/course`,
      providesTags: (_courses, _error, userId) => [
        { type: "course", id: "LIST" },
        { type: "user", id: userId },
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useCreateCourseMutation,
  useGetCoursesQuery,
  useUpdateCourseMutation,
  useDeleteCourseMutation,
  useGetCourseQuery,
  useEnrollCourseMutation,
  useLeaveCourseMutation,
  useGetDashboardQuery,
  useAdminEnrollUserToCourseMutation,
  useAdminUnenrollUserFromCourseMutation,
  useGetUserEnrolledCoursesQuery,
  useLazyGetUserEnrolledCoursesQuery,
} = course;
